import { FadeUp } from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

// SVG payment logos inlined (mono, single fill) — clean editorial style
const Logo = ({ label, children }) => (
  <div className="group relative border border-neutral-300 bg-white h-20 flex flex-col items-center justify-center gap-1.5 px-4 hover:border-black transition-colors duration-300">
    <div className="h-6 flex items-center text-neutral-800 group-hover:text-black">
      {children}
    </div>
    <div className="font-mono-label text-[9px] text-neutral-500">{label}</div>
  </div>
);

export default function PaymentStrip() {
  const { lang } = useI18n();
  const nl = lang === "nl";
  return (
    <section data-testid="payment-strip" className="py-24 md:py-28 bg-[#F7F7F6] border-y border-neutral-300">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-3">
            <p className="font-mono-label text-[11px] text-neutral-500">[ 05 · PAYMENTS ]</p>
          </div>
          <div className="md:col-span-9">
            <FadeUp>
              <h2 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.02]">
                {nl ? "Betaal veilig met de methode die jij kent." : "Pay securely with the method you trust."}
              </h2>
              <p className="mt-6 max-w-2xl text-neutral-600">
                {nl
                  ? "Verwerkt via Stripe — Europees, PSD2 compliant, versleuteld. Kaartgegevens raken onze servers nooit."
                  : "Processed via Stripe — European, PSD2 compliant, encrypted. Card details never touch our servers."}
              </p>
            </FadeUp>
          </div>
        </div>

        <FadeUp>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <Logo label="iDEAL">
              <svg viewBox="0 0 60 24" width="52" height="20" fill="currentColor">
                <text x="0" y="17" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="18" letterSpacing="-1">iDEAL</text>
              </svg>
            </Logo>
            <Logo label="Card">
              <svg viewBox="0 0 40 24" width="46" height="20" fill="currentColor">
                <text x="0" y="17" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="18" letterSpacing="-1">VISA</text>
              </svg>
            </Logo>
            <Logo label="Card">
              <svg viewBox="0 0 40 24" width="26" height="20">
                <circle cx="14" cy="12" r="9" fill="currentColor" opacity="0.85" />
                <circle cx="24" cy="12" r="9" fill="currentColor" opacity="0.5" />
              </svg>
            </Logo>
            <Logo label="Buy now, pay later">
              <svg viewBox="0 0 60 24" width="60" height="20" fill="currentColor">
                <text x="0" y="17" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="15" letterSpacing="0.5">KLARNA</text>
              </svg>
            </Logo>
            <Logo label="Wallet">
              <svg viewBox="0 0 68 24" width="60" height="20" fill="currentColor">
                <text x="0" y="17" fontFamily="Inter, sans-serif" fontStyle="italic" fontWeight="700" fontSize="16" letterSpacing="-0.5">PayPal</text>
              </svg>
            </Logo>
            <Logo label="Bank transfer">
              <svg viewBox="0 0 60 24" width="52" height="20" fill="currentColor">
                <text x="0" y="17" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="14" letterSpacing="0.5">SEPA · BUNQ</text>
              </svg>
            </Logo>
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 font-mono-label text-[10px] text-neutral-500">
            <div className="border-t border-neutral-300 pt-3">[ 3DS ] Strong customer auth</div>
            <div className="border-t border-neutral-300 pt-3">[ SSL ] End-to-end encrypted</div>
            <div className="border-t border-neutral-300 pt-3">[ EU ] PSD2 compliant</div>
            <div className="border-t border-neutral-300 pt-3">[ PCI ] DSS Level 1 · Stripe</div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
